// PageHeaderContext lets a page render its own header content INTO
// the dashboard's top-bar (the AppHeader chrome strip that holds the
// hamburger + user dropdown), instead of rendering a SECOND header
// below the chrome — which left a wasted empty strip on top of the
// page's actual header.
//
// Pages opt in via the <PageHeader> helper:
//
//   <PageHeader>
//       <MyCustomHeader actionsForThisPage … />
//   </PageHeader>
//
// TopHeader reads the current content from the context and renders
// it as its children when set; otherwise it falls back to its
// path-based pageConfig title. Provider lives in the DashboardShell
// (App.tsx) so every dashboard page can opt in without app-level
// plumbing per page.

import React, { createContext, useContext, useEffect, useState } from 'react';

interface PageHeaderState {
    content: React.ReactNode;
    setContent: (c: React.ReactNode) => void;
}

const PageHeaderContext = createContext<PageHeaderState>({
    content: null,
    setContent: () => {},
});

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [content, setContent] = useState<React.ReactNode>(null);
    return (
        <PageHeaderContext.Provider value={{ content, setContent }}>
            {children}
        </PageHeaderContext.Provider>
    );
};

/** Read the currently-injected page-header content. Used by TopHeader. */
export const usePageHeaderContent = (): React.ReactNode =>
    useContext(PageHeaderContext).content;

/** Render children into the top-bar chrome strip. Sets on mount/update,
 *  clears on unmount. No deps on the useEffect — fires every render of
 *  the consuming page, which is cheap (one state update); React skips
 *  the consumer re-render when the new node is reference-equal. Wrap
 *  the children in useMemo if your page re-renders frequently and you
 *  notice TopHeader thrashing. */
export const PageHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { setContent } = useContext(PageHeaderContext);
    useEffect(() => {
        setContent(children);
        return () => setContent(null);
    });
    return null;
};
